import React, { useEffect, useRef, useCallback, useMemo, useState, useLayoutEffect } from "react";
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { Highlight, themes } from "prism-react-renderer";
import Prism from "prismjs";
import "prismjs/components/prism-python";

import * as lessonParser from "utils/lessonParser";
import AIExplanation, { AIExplainIcon } from "components/AIExplanation";
import PopupTooltip from "components/PopupTooltip";
import ChallengeTag from "components/ChallengeTag";



const theme = themes.dracula;
console.log(theme);




const tag = (i) => `__SEGMENT_${i}__`;
const inputTag = (i) => `__INPUT_${i}__`;

const getCaretOffset = (root) => {
    const sel = window.getSelection();
    if (!sel || sel.rangeCount === 0) return 0;
    const range = sel.getRangeAt(0);
    let chars = 0;
    const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
    while (walker.nextNode()) {
        const n = walker.currentNode;
        if (n === range.endContainer) return chars + range.endOffset;
        chars += n.textContent.length;
    }
    return chars;
};
const setCaretOffset = (root, offset) => {
    const sel = window.getSelection();
    if (!sel) return;
    let chars = 0;
    const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
    while (walker.nextNode()) {
        const n = walker.currentNode;
        const next = chars + n.textContent.length;
        if (next >= offset) {
            const rng = document.createRange();
            rng.setStart(n, offset - chars);
            rng.collapse(true);
            sel.removeAllRanges();
            sel.addRange(rng);
            return;
        }
        chars = next; //gtg dinner 
    }
    root.focus();
};




function Editable({
    segIdx,
    length,
    disabled,
    outerColor,
    highlightInside,
    onEdit,
    autoFocus,
    correct,
    realAnswer = "",
    forceValue = "",
    dimLine = false
}) {
    const ref = useRef(null);

    const repaint = useCallback(() => {
        if (!ref.current) return;
        const isFocused = document.activeElement === ref.current;
        const caret = isFocused ? getCaretOffset(ref.current) : 0;


        const clone = ref.current.cloneNode(true);
        clone.querySelectorAll('.realAnswerDisplay').forEach(el => el.remove());
        const plain = clone.textContent;

        let renderedText = forceValue || plain;

        const mainHTML =
            `<span>` +
            (
                highlightInside && renderedText
                    ? Prism.highlight(String(renderedText), Prism.languages.python, "python")
                    : String(renderedText)
                        .replace(/&/g, "&amp;")
                        .replace(/</g, "&lt;")
                        .replace(/>/g, "&gt;")
            ) +
            `</span>`;

        // const highlighted =
        //     `<span class="realAnswerDisplay" style="opacity: 0.6; margin-left: ${plain ? 17 : 0}px; background:hsl(356deg 89% 55% / 30%); padding:1px 2px; border-radius: 4px;">` +
        //     (highlightInside

        //         ? Prism.highlight(realAnswer, Prism.languages.python, "python")
        //         : realAnswer
        //             .replace(/&/g, "&amp;")
        //             .replace(/</g, "&lt;")
        //             .replace(/>/g, "&gt;")) +
        //     `</span>`;

        // ref.current.innerHTML = mainHTML + (realAnswer ? highlighted : "");
        ref.current.innerHTML = mainHTML;

        // Apply the correct Dracula theme styles
        const tokens = ref.current.querySelectorAll("span.token");
        tokens.forEach((token) => {
            const tokenType = token.className.split(" ").find((cls) => cls !== "token");
            if (tokenType) {
                const style = theme.styles.find((s) => s.types.includes(tokenType.replace("token ", "")));
                if (style) {
                    Object.assign(token.style, style.style);
                }
            }
        });

        if (isFocused) setCaretOffset(ref.current, caret);
    }, [highlightInside, realAnswer]);

    useEffect(() => {

        repaint();

    }, [realAnswer]);

    const handleKeyDown = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            return;               // bail early
        }
    };

    const handlePaste = (e) => {
        e.preventDefault();
        const text = (e.clipboardData || window.clipboardData).getData('text');
        const sanitized = text.replace(/[\r\n]+/g, ' ');
        const sel = window.getSelection();
        if (!sel || sel.rangeCount === 0) return;
        const range = sel.getRangeAt(0);
        range.deleteContents();
        range.insertNode(document.createTextNode(sanitized));
        range.collapse(false);
        sel.removeAllRanges();
        sel.addRange(range);
        onEdit(segIdx, ref.current.textContent);
        repaint();
    };

    const handleInput = (e) => {

        onEdit(segIdx, ref.current.textContent);
        repaint();
    };

    const moveCaretToEnd = () =>
        setTimeout(() => {
            if (ref.current) setCaretOffset(ref.current, ref.current.textContent.length);
        });

    useEffect(() => {
        if (autoFocus && ref.current) {
            ref.current.focus();
            moveCaretToEnd();
        }
    }, [autoFocus]);

    return (
        <span
            ref={ref}
            contentEditable={!disabled}
            suppressContentEditableWarning
            onInput={handleInput}
            onKeyDown={handleKeyDown}
            autoCapitalize="off"
            autoCorrect="off"
            spellCheck={false}
            autoComplete="off"
            onPaste={handlePaste}
            onMouseDown={moveCaretToEnd}
            onFocus={moveCaretToEnd}
            style={{
                whiteSpace: "pre",
                color: outerColor,
                caretColor: outerColor,
                minWidth: `${Math.max(length, 1)}ch`,
                textAlign: "left",
                borderBottom: `2px solid ${disabled ? (`color-mix(in srgb, ${correct ? "var(--accent)" : "var(--warning)"} ${dimLine ? "25" : "100"}%, transparent)`) : "var(--border)"}`,
            }}
            className={`inline-block border-none transition-[border-color] outline-none ${!disabled && "_inputBorderEditable"}`}
        />
    );
}


const customStyle = {
    width: "fit-content",
    borderRadius: '0',
    backgroundColor: 'transparent',
    padding: '0',
    margin: '0',
    fontSize: '20px'
};





export default function InputBox({ questionData, questionVariables, questionText, questionStatus, onAnswerCheck, onCheckValid, onHelpUsed, tag: challengeTag }) {
    const [aiExplainCache, setAiExplainCache] = useState({});

    const lessonScrollRef = useRef(null);
    const aiRef = useRef(null);
    const popupRef = useRef(null);

    const inputs = useRef({});

    function getWrongInputIndex(viewIdx) {
        let totalInput = 1;
        let wrong = 0;
        for (let i = 0; i < questionData.data.segments.length; i++) {
            if (questionData.data.segments[i].type === "input") {
                console.log("input index:", totalInput, "View index:", viewIdx, "Current wrong count:", wrong);
                if (
                    inputs.current[i] &&
                    inputs.current[i].correct === false) {
                    wrong++;
                    if (wrong === viewIdx) return totalInput;   // ← segment index == global input #
                }
                totalInput++;
            }
        }
        return null;
    }

    const source = questionData?.data?.segments
        ? questionData.data.segments
            .map((s, i) => (s.type === "input" ? ` ${tag(i)} ` : lessonParser.replaceVariablesInText(s.data, questionVariables)))
            .join("")
        : "";

    useEffect(() => {
        if (!questionData?.data?.segments) return;
        let overall = true;
        questionData.data.segments.forEach((s, i) => {
            if (s.type === "input" && s.val) {
                let valid = lessonParser.checkFunc(s.val);
                if (!valid) {
                    overall = false;
                }
            }
        });
        onCheckValid?.(() => {
            return overall ? null : "Invalid validator function(s) ";
        });
    }, [questionData]);

    function getExpectedFromValidators(segment, questionVariables) {
        let expected = ""
        valLoop: for (let j = 0; j < segment.val.length; j++) {
            let validator = segment.val[j];

            if (validator.params?.expected) {
                if (Array.isArray(validator.params.expected)) {
                    const firstItem = validator.params.expected[0];
                    // Check if it's an object that needs parsing (like {type: "value", variables: {...}, value: "..."})
                    if (typeof firstItem === 'object' && firstItem !== null && firstItem.type) {
                        expected = lessonParser.parseValue(firstItem, questionVariables).value?.toString() || "";
                    } else {
                        expected = lessonParser.replaceVariablesInText(firstItem, questionVariables);
                    }
                    break valLoop;
                } else if (typeof validator.params.expected === "string") {
                    expected = lessonParser.replaceVariablesInText(validator.params.expected, questionVariables);
                    break valLoop;
                } else if (typeof validator.params.expected === "object" && validator.params.expected !== null && validator.params.expected.type) {
                    // Handle case where expected is directly an object (not in array)
                    expected = lessonParser.parseValue(validator.params.expected, questionVariables).value?.toString() || "";
                    break valLoop;
                }
            }
        }

        return expected;

    }

    const handleHelpClick = () => {
        // Notify parent that help was used
        onHelpUsed?.();
        let inputIdx = 1;
        const preCompiled = questionData.data.segments
            .map((s) => {
                if (s.type === "input") {
                    const tag = inputTag(inputIdx) || "";
                    inputIdx++;
                    return tag;
                } else {
                    return lessonParser.replaceVariablesInText(s.data, questionVariables);
                }
            })
            .join("");
        // Get all user inputs and expected answers for AI context
        const inputsCompiled = questionData.data.segments
            .map((s, i) => (s.type === "input" ?
                inputs.current[i]?.value || "__BLANK__" : lessonParser.replaceVariablesInText(s.data, questionVariables))).join("");
        const correctCompiled = questionData.data.segments
            .map((s, i) => (s.type === "input" ?
                getExpectedFromValidators(s, questionVariables) || "" : lessonParser.replaceVariablesInText(s.data, questionVariables))).join("");
        // Trigger AI explanation
        if (!aiExplainCache[inputTag(getWrongInputIndex(incorrectViewIdx + 1))]?.started) {
            setAiExplainCache((prev) => ({
                ...prev,
                [inputTag(getWrongInputIndex(incorrectViewIdx + 1))]: {
                    started: true,
                }
            }));
            aiRef.current?.getAIExplanation(questionData, {
                question: questionText,
                preCompiled,
                inputsCompiled,
                correctCompiled,
                inputTag: inputTag(getWrongInputIndex(incorrectViewIdx + 1))
            }, true).then((output) => {
                console.log("AI Explanation Output:", output);

                setAiExplainCache((prev) => ({
                    ...prev,
                    [inputTag(getWrongInputIndex(incorrectViewIdx + 1))]: {
                        started: true,
                        explanation: output
                    }
                }));
            }).catch((err) => {
                console.error("AI Explanation Error:", err);
            })
        }
        aiRef.current?.showSection();
        setTimeout(() => {
            if (lessonScrollRef.current) {
                lessonScrollRef.current.scrollTo({
                    top: lessonScrollRef.current.scrollHeight,
                    behavior: 'smooth'
                });
            }
        }, 200);
    };

    useEffect(() => {
        onAnswerCheck((force) => {
            let correctSlots = []

            let anyEdited = questionData.data.segments.find((s, i) => s.type === "input" && inputs.current[i] && inputs.current[i].value !== "");
            if (!anyEdited && !force) {
                return null;
            }


            questionData.data.segments.forEach((s, i) => {
                if (s.type === "input") {
                    let parsedData = lessonParser.parseValueList(
                        s.val,
                        false,
                        questionVariables,
                        null,
                        { inputCode: inputs.current[i].value || "" }
                    );

                    let allCorrectInSlot = parsedData.every((d) => d.value.success);


                    if (allCorrectInSlot) {
                        correctSlots[i] = true;
                        inputs.current[i] = { value: inputs.current[i].value, correct: true };

                    } else {
                        correctSlots[i] = false;

                        let customExpected = s?.expected ? lessonParser.parseValue(s?.expected, questionVariables)?.value?.toString() || "" : null;
                        let expected = customExpected ? customExpected : getExpectedFromValidators(s, questionVariables);

                        inputs.current[i] = { value: inputs.current[i].value, correct: false, realAnswer: expected || "?" };

                    }


                }
            });

            const allInputs = correctSlots.every((s) => s === true || s === undefined);

            setTimeout(() => {
                if (lessonScrollRef.current) {
                    lessonScrollRef.current.scrollTo({
                        top: lessonScrollRef.current.scrollHeight,
                        behavior: 'smooth'
                    });
                }
            }, 10);

            // Trigger AI explanation on incorrect answer
            if (!allInputs && !force) {
                requestAnimationFrame(() => {
                    let inputIdx = 1;
                    const preCompiled = questionData.data.segments
                        .map((s) => {
                            if (s.type === "input") {
                                const tag = inputTag(inputIdx) || "";
                                inputIdx++;
                                return tag;
                            } else {
                                return lessonParser.replaceVariablesInText(s.data, questionVariables);
                            }
                        })
                        .join("");
                    const inputsCompiled = questionData.data.segments
                        .map((s, i) => (s.type === "input" ?
                            inputs.current[i]?.value || "__BLANK__" : lessonParser.replaceVariablesInText(s.data, questionVariables))).join("");
                    const correctCompiled = questionData.data.segments
                        .map((s, i) => (s.type === "input" ?
                            getExpectedFromValidators(s, questionVariables) || "" : lessonParser.replaceVariablesInText(s.data, questionVariables))).join("");
                    // Trigger AI explanation
                    setAiExplainCache((prev) => ({
                        ...prev,
                        [inputTag(getWrongInputIndex(incorrectViewIdx + 1))]: {
                            started: true,
                        }
                    }));
                    aiRef.current?.getAIExplanation(questionData, {
                        question: questionText,
                        preCompiled,
                        inputsCompiled,
                        correctCompiled,
                        inputTag: inputTag(getWrongInputIndex(incorrectViewIdx + 1))
                    }, true).then((output) => {
                        console.log("AI Explanation Output:", output);

                        setAiExplainCache((prev) => ({
                            ...prev,
                            [inputTag(getWrongInputIndex(incorrectViewIdx + 1))]: {
                                started: true,
                                explanation: output
                            }
                        }));

                        setTimeout(() => {
                            if (lessonScrollRef.current) {
                                lessonScrollRef.current.scrollTo({
                                    top: lessonScrollRef.current.scrollHeight,
                                    behavior: 'smooth'
                                });
                            }
                        }, 200);
                    }).catch((err) => {
                        console.error("AI Explanation Error:", err);
                    })
                });
            }

            return { isCorrect: allInputs }
        });
    }, [onAnswerCheck, questionData]);


    const [incorrectViewIdx, setIncorrectViewIdx] = useState(0);

    function getNumLines() {
        if (!questionData?.data?.segments) return 1;
        let lines = 0;
        const segs = questionData.data.segments;
        for (let i = 0; i < segs.length; i++) {
            if (segs[i].type !== "input") {
                const text = lessonParser.replaceVariablesInText(segs[i].data, questionVariables);
                lines += text.split("\n").length - 1;
            }
        }
        return lines + 1;
    }
    function getIncorrectLines() {
        if (!questionData?.data?.segments) return [];
        let lines = [];
        const segs = questionData.data.segments;
        let line = 0;
        for (let i = 0; i < segs.length; i++) {
            if (segs[i].type === "input") {
                if (!lines.includes(line) && inputs.current[i] && inputs.current[i].correct === false) {
                    lines.push(line);
                }
            } else {
                const text = lessonParser.replaceVariablesInText(segs[i].data, questionVariables);
                line += text.split("\n").length - 1;

            }
        }
        return lines;
    }

    const incorrectLines = useMemo(() => getIncorrectLines(), [questionData, inputs]);



    function getLineFromIndex(index) {
        if (!questionData?.data?.segments) return 0;
        const segs = questionData.data.segments;
        let line = 0;
        let inputCount = 0;
        let lastLine = null;
        for (let i = 0; i < segs.length; i++) {
            if (segs[i].type === "input") {
                if (inputs.current[i] && inputs.current[i].correct === false) {
                    if (lastLine !== null && lastLine === line) {
                        lastLine = line;
                        // line++;
                        continue
                    }
                    lastLine = line;
                    if (index === inputCount) return line;
                    inputCount++;
                }
            } else {
                const text = lessonParser.replaceVariablesInText(segs[i].data, questionVariables);
                line += text.split("\n").length - 1;
            }
        }
    }

    const scrollAnimRef = useRef(null);
    const incorrectScrollElRef = useRef(null);

    // const easeInOut = (p) => (p < 0.5 ? 2 * p * p : -1 + (4 - 2 * p) * p);
    const easeOut = (p) => 1 - Math.pow(1 - p, 12);

    const animateScrollTop = (el, targetY, duration = 300) => {
        if (!el) return;

        if (scrollAnimRef.current) {
            cancelAnimationFrame(scrollAnimRef.current);
            scrollAnimRef.current = null;
        }

        const startY = el.scrollTop;
        const delta = targetY - startY;
        if (delta === 0) {
            // console.log("No scroll needed, target is already at the desired position.");
            return
        };

        const startTime = performance.now();

        const step = (now) => {
            const raw = Math.min((now - startTime) / duration, 1); // 0–1
            // const eased = easeInOut(raw);                            // curved 0–1
            const eased = easeOut(raw);                            // curved 0–1
            el.scrollTop = startY + delta * eased;


            // if (scrollPendingRef.current && raw > 0.1 && !finished) {
            //     scrollPendingRef.current = false;
            //     console.log("Scroll animation completed");
            //     finished = true;
            // }

            if (raw < 1) {
                scrollAnimRef.current = requestAnimationFrame(step);
            }
        };

        scrollAnimRef.current = requestAnimationFrame(step);
    };

    const scrollToLine = (line) => {
        if (!incorrectScrollElRef.current) return;

        const divs = incorrectScrollElRef.current.querySelectorAll("div");
        const target = divs[line];
        if (!target) return;

        let isLastLine = target === divs[divs.length - 1];
        const parentH = incorrectScrollElRef.current.parentNode?.clientHeight ?? 0;
        const y = Math.max(target.offsetTop - (isLastLine ? target.clientHeight * 2 : parentH / 2 - target.clientHeight / 2 + 1), 0);

        // const distance = Math.abs(node.scrollTop - y);
        // const duration = Math.round(distance * MS_PER_PX);
        const duration = 1000;

        animateScrollTop(incorrectScrollElRef.current, y, duration);     // <— custom smooth scroll
    };

    const linesLenRef = useRef(0);      // latest incorrect-line count
    const lastScrollTimeRef = useRef(0);      // simple 100 ms gate
    const wheelHandlerRef = useRef(null);

    /* ---------- keep the line count fresh each render ---------- */
    useEffect(() => {
        linesLenRef.current = getIncorrectLines()?.length ?? 0;
    });


    useLayoutEffect(() => {
        if (!incorrectScrollElRef.current) return;
        scrollToLine(getLineFromIndex(incorrectViewIdx));
        if (questionStatus["guessed"] && !questionStatus["answer"]) {
            // generate new ai explanation for the new incorrect line selected
            let inputIdx = 1;
            const preCompiled = questionData.data.segments
                .map((s) => {
                    if (s.type === "input") {
                        const tag = inputTag(inputIdx) || "";
                        inputIdx++;
                        return tag;
                    } else {
                        return lessonParser.replaceVariablesInText(s.data, questionVariables);
                    }
                })
                .join("");
            const inputsCompiled = questionData.data.segments
                .map((s, i) => (s.type === "input" ?
                    inputs.current[i]?.value || "__BLANK__" : lessonParser.replaceVariablesInText(s.data, questionVariables))).join("");
            const correctCompiled = questionData.data.segments
                .map((s, i) => (s.type === "input" ?
                    getExpectedFromValidators(s, questionVariables) || "" : lessonParser.replaceVariablesInText(s.data, questionVariables))).join("");
            // Trigger AI explanation
            if (aiExplainCache[inputTag(getWrongInputIndex(incorrectViewIdx + 1))]?.started) {
                console.log("AI explanation already started for this input tag, using cache", aiExplainCache[inputTag(getWrongInputIndex(incorrectViewIdx + 1))]);
                if (aiExplainCache[inputTag(getWrongInputIndex(incorrectViewIdx + 1))]?.explanation) {
                    aiRef.current?.setExplanation(aiExplainCache[inputTag(getWrongInputIndex(incorrectViewIdx + 1))].explanation);
                }
                return
            }; // already started
            setAiExplainCache((prev) => ({
                ...prev,
                [inputTag(getWrongInputIndex(incorrectViewIdx + 1))]: {
                    started: true,
                }
            }));
            setTimeout(() => {
                if (lessonScrollRef.current) {
                    lessonScrollRef.current.scrollTo({
                        top: lessonScrollRef.current.scrollHeight,
                        behavior: 'smooth'
                    });
                }
            }, 200);
            aiRef.current?.getAIExplanation(questionData, {
                question: questionText,
                preCompiled,
                inputsCompiled,
                correctCompiled,
                inputTag: inputTag(getWrongInputIndex(incorrectViewIdx + 1))
            }, true).then((output) => {
                console.log("AI Explanation Output:", output);

                setAiExplainCache((prev) => ({
                    ...prev,
                    [inputTag(getWrongInputIndex(incorrectViewIdx + 1))]: {
                        started: true,
                        explanation: output
                    }
                }));
                setTimeout(() => {
                    if (lessonScrollRef.current) {
                        lessonScrollRef.current.scrollTo({
                            top: lessonScrollRef.current.scrollHeight,
                            behavior: 'smooth'
                        });
                    }
                }, 200);
            }).catch((err) => {
                console.error("AI Explanation Error:", err);
            })
        }
    }, [incorrectViewIdx]);

    /* ---------- ONE stable function that moves the index ---------- */
    const moveIncorrectIdx = useCallback(dir => {
        const now = performance.now();
        if (now - lastScrollTimeRef.current < 100) return;     // throttle first
        lastScrollTimeRef.current = now;

        setIncorrectViewIdx(prev => {
            const next = Math.min(
                Math.max(prev + dir, 0),
                linesLenRef.current - 1
            );
            return next === prev ? prev : next;                  // React skips identical
        });
    }, []);   // stays identical for the lifetime of the component

    /* ---------- attach / detach the wheel handler whenever the node changes ---------- */
    const attachScrollEl = useCallback(node => {
        /* detach from the previous node (if any) */
        if (wheelHandlerRef.current && incorrectScrollElRef.current) {
            incorrectScrollElRef.current.removeEventListener('wheel', wheelHandlerRef.current);
            wheelHandlerRef.current = null;
        }

        /* store the new node (might be null on unmount) */
        incorrectScrollElRef.current = node;
        if (!node) return;

        /* create a fresh handler that closes over the stable moveIncorrectIdx */
        const wheelHandler = e => {
            if (e.ctrlKey || e.metaKey || e.deltaY === 0) return;
            e.preventDefault();
            moveIncorrectIdx(e.deltaY > 0 ? 1 : -1);
        };

        node.addEventListener('wheel', wheelHandler, { passive: false });
        wheelHandlerRef.current = wheelHandler;

        /* centre the first selected line */
        requestAnimationFrame(() => {
            scrollToLine(getLineFromIndex(incorrectViewIdx));
        });
    }, [moveIncorrectIdx, incorrectViewIdx]);



    return (
        <div ref={lessonScrollRef} className='text-text flex justify-center w-full overflow-auto max-h-full py-4 pt-16 sm:pt-4'>

            <div className='flex items-center flex-col w-full h-full break-small:w-min '>
                <div className="flex justify-center w-full">
                    <div className='px-10 break-small:px-0 w-full text-center break-small:text-left'>
                        {challengeTag && <div className='mb-3'><ChallengeTag tag={challengeTag} /></div>}
                        <h1 className='font-bold text-[5vw] break-small:text-4xl mb-[4vh] break-big:mb-[6vh]'>{questionText}</h1>
                    </div>
                </div>
                <div
                    className="min-w-fit w-fit flex flex-col gap-8"
                >
                    <div
                        className="InputBox text-xl w-[600px] break-small:w-[700px] break-big:w-[800px] max-h-[400px] h-[50vh] bg-background-light
                 rounded-xl border-[3px] border-border p-4 overflow-auto
                 text-left transition-[border-color]"
                        style={{ borderColor: questionStatus["guessed"] ? (questionStatus["answer"] ? "var(--accent)" : "var(--warning)") : "var(--border)" }}
                    >
                        <Highlight code={source} language="python" theme={theme}>
                            {({ className, style, tokens, getLineProps, getTokenProps }) => {
                                let inputCount = -1;
                                return (
                                    <pre
                                        className={`${className}  `}     /* ← and the <pre> itself */
                                        style={{ ...style, color: "white", padding: "0.4rem", margin: 0, textAlign: "left", background: "var(--code-light)", borderRadius: "0.5rem", width: "100%", height: "100%", overflowX: "auto" }}
                                    >
                                        {tokens.map((line, li) => (
                                            <div key={li} {...getLineProps({ line })}>
                                                <span className="select-none mr-4 opacity-60 ">
                                                    {String(li + 1).padStart(2, " ")}
                                                </span>

                                                {line.map((token, ti) => {
                                                    const parts = [];
                                                    const re = / ?__SEGMENT_(\d+)__ ?/g;
                                                    let last = 0,
                                                        m;
                                                    while ((m = re.exec(token.content)) !== null) {
                                                        if (m.index > last)
                                                            parts.push({ t: "txt", v: token.content.slice(last, m.index) });
                                                        parts.push({ t: "blank", idx: Number(m[1]) });
                                                        last = m.index + m[0].length;
                                                    }
                                                    if (last < token.content.length)
                                                        parts.push({ t: "txt", v: token.content.slice(last) });

                                                    return parts.flatMap((p, pi) => {
                                                        if (p.t === "blank") {
                                                            const { style: st = {}, className: cl = "" } = getTokenProps({ token });
                                                            const inString = /token string|token comment/.test(cl);
                                                            const seg = questionData.data.segments[p.idx];

                                                            // On first render, initialize the value to ""
                                                            if (inputs.current[p.idx] === undefined) {
                                                                inputs.current[p.idx] = { value: "" };
                                                            }
                                                            let customExpected = seg?.expected ? lessonParser.parseValue(seg?.expected, questionVariables).value?.toString() || "" : null;
                                                            let customLength = seg.chars ? seg.chars : (customExpected ? customExpected.length : null);
                                                            let defExpected = getExpectedFromValidators(seg, questionVariables);
                                                            let inputLength = customLength ? customLength : lessonParser.replaceVariablesInText(Array.isArray(defExpected) ? defExpected?.[0] : defExpected ?? 7, questionVariables)?.length || 7;

                                                            inputCount++;

                                                            return (
                                                                <Editable
                                                                    key={`seg-${p.idx}`}
                                                                    segIdx={p.idx}
                                                                    length={Math.min(inputLength, 30)}
                                                                    disabled={questionStatus.guessed}
                                                                    outerColor={st.color || "inherit"}
                                                                    highlightInside={!inString}
                                                                    autoFocus={inputCount === 0}
                                                                    onEdit={(i, val) => (inputs.current[i].value = val)}
                                                                    correct={questionStatus["guessed"] && inputs.current[p.idx].correct}
                                                                    realAnswer={inputs.current[p.idx].realAnswer}
                                                                />
                                                            );
                                                        }
                                                        const mini = { ...token, content: p.v };
                                                        return (
                                                            <span key={`${ti}-${pi}`} {...getTokenProps({ token: mini })} />
                                                        );
                                                    });
                                                })}
                                            </div>
                                        ))}
                                    </pre>)
                            }}
                        </Highlight>

                    </div>
                    {(questionStatus["guessed"] && !questionStatus["answer"]) && <div className='flex-shrink w-full min-h-40 rounded-xl bg-warning-darker border-warning border-[3px] flex p-6 px-6  flex-col gap-5 mb-12' style={{ animation: "incorrectSection 450ms ease" }}>
                        <div className='flex flex-col sm:flex-row items-center justify-between'>
                            <h1 className='text-text text-2xl font-bold text-left'>Correct Answer:</h1>
                            <button
                                className='pr-1 text-primary text-lg font-bold text-right flex items-center gap-2 outline-none hover:underline relative'
                                onClick={handleHelpClick}
                            ><AIExplainIcon /> See how to get this answer
                                <PopupTooltip
                                    ref={popupRef}
                                    text="Stuck? Use free AI to help you understand it better!"
                                    open={questionStatus["guessed"] && !questionStatus["answer"]}
                                    storageKey="seenHelpPopup"
                                    autoMarkSeenAfter={2000}
                                />
                            </button>
                        </div>
                        <div className=" select-none rounded-[16px] border-[3px] border-border w-full  bg-background-light  flex flex-col group h-full drop-shadow-2xl p-2 drop">
                            <div className="max-w-full w-full relative bg-code text-lg p-2 py-2 pb-1 rounded-[8px]  flex overflow-hidden" style={{ height: source.split("\n").length > 2 ? "6rem" : source.split("\n").length > 1 ? "4.8rem" : "3rem" }}>
                                <div className="pointer-events-none absolute top-0 left-0 w-full z-10 transition-all duration-200 ease-out"
                                    style={{
                                        background: 'linear-gradient(to bottom,color-mix(in srgb, var(--code) 100%, transparent) 10%,transparent)',
                                        opacity: getLineFromIndex(incorrectViewIdx) === 0 ? 0 : 1,
                                        height: (getLineFromIndex(incorrectViewIdx) === getNumLines() - 1 && getNumLines() > 2) ? "4rem" : "2.5rem",
                                    }} />
                                <div className="pointer-events-none absolute top-0 left-0 w-full h-8 z-10 transition-all duration-200 ease-out"
                                    style={{
                                        backdropFilter: 'blur(1px)',
                                        maskImage: 'linear-gradient(to bottom, black 20%, transparent)', // top fade
                                        WebkitMaskImage: 'linear-gradient(to bottom, black 20%, transparent)',
                                        opacity: getLineFromIndex(incorrectViewIdx) === 0 ? 0 : 1,
                                        height: (getLineFromIndex(incorrectViewIdx) === getNumLines() - 1 && getNumLines() > 2) ? "4rem" : "2.5rem",
                                    }} />

                                <Highlight code={source} language="python" theme={theme}>
                                    {({ className, style, tokens, getLineProps, getTokenProps }) => (
                                        <pre
                                            className={`${className}  `}
                                            ref={attachScrollEl}
                                            style={{ ...style, margin: 0, textAlign: "left", background: 'transparent', width: "100%", maxWidth: "100%", maxHeight: "100%", overflowX: "hidden", overflowY: "hidden", paddingBottom: "0rem" }}
                                        >
                                            {tokens.map((line, li) => (
                                                <div key={li} {...getLineProps({ line })}>
                                                    <span className="select-none mr-4 opacity-60 ">
                                                        {String(li + 1).padStart(2, " ")}
                                                    </span>

                                                    {line.map((token, ti) => {
                                                        const parts = [];
                                                        const re = / ?__SEGMENT_(\d+)__ ?/g;
                                                        let last = 0,
                                                            m;
                                                        while ((m = re.exec(token.content)) !== null) {
                                                            if (m.index > last)
                                                                parts.push({ t: "txt", v: token.content.slice(last, m.index) });
                                                            parts.push({ t: "blank", idx: Number(m[1]) });
                                                            last = m.index + m[0].length;
                                                        }
                                                        if (last < token.content.length)
                                                            parts.push({ t: "txt", v: token.content.slice(last) });

                                                        return parts.flatMap((p, pi) => {
                                                            if (p.t === "blank") {
                                                                const { style: st = {}, className: cl = "" } = getTokenProps({ token });
                                                                const inString = /token string|token comment/.test(cl);
                                                                const seg = questionData.data.segments[p.idx];

                                                                let customExpected = seg?.expected ? lessonParser.parseValue(seg?.expected, questionVariables).value?.toString() || "" : null;
                                                                let expected = customExpected ? customExpected : getExpectedFromValidators(seg, questionVariables);
                                                                // console.log("Expected value for segment:", expected, "from segment:", seg);


                                                                return (
                                                                    <Editable
                                                                        key={`incorrect-seg-${p.idx}`}
                                                                        segIdx={p.idx}
                                                                        length={expected.length}
                                                                        disabled={questionStatus.guessed}
                                                                        outerColor={st.color || "inherit"}
                                                                        highlightInside={!inString}
                                                                        correct={questionStatus["guessed"] && inputs.current[p.idx].correct}
                                                                        forceValue={expected}
                                                                        dimLine={getLineFromIndex(incorrectViewIdx) !== li}
                                                                    />
                                                                );
                                                            }
                                                            const mini = { ...token, content: p.v };
                                                            return (
                                                                <span key={`${ti}-${pi}`} {...getTokenProps({ token: mini })} />
                                                            );
                                                        });
                                                    })}
                                                </div>
                                            ))}

                                        </pre>
                                    )}
                                </Highlight>
                                {getIncorrectLines().length > 1 && <div className="w-[1.6rem] absolute right-2 bottom-2 top-2 rounded-[.6rem] bg-background-light z-20 flex flex-col items-center p-[0.3rem] gap-[0.2rem]">
                                    {getIncorrectLines()?.map((s, i) => {
                                        let selected = i === incorrectViewIdx;
                                        let lines = getIncorrectLines() ?? [];
                                        return (
                                            <div
                                                key={i}
                                                className={`cursor-pointer w-full rounded-[.4rem] transition-all duration-[400ms] ease-out`}
                                                style={{ background: selected ? "rgb(226 232 240)" : "var(--border-light)", flexGrow: selected ? 1 : 0, flexShrink: selected ? 0 : 1, minHeight: selected ? (lines?.length > 5 ? "1rem" : "1.5rem") : (lines?.length > 6 ? "0.25rem" : (lines?.length > 4 ? "0.5rem" : (lines?.length > 3 ? "0.7rem" : ((lines?.length > 2 ? "1rem" : "1rem"))))) }}
                                                onClick={(e) => {
                                                    setIncorrectViewIdx(i);
                                                    scrollToLine(getLineFromIndex(i));
                                                }}
                                            />

                                        );
                                    })}
                                </div>}
                                <div className="pointer-events-none absolute bottom-0 left-0 w-full h-10 z-10 transition-all duration-200 ease-out"
                                    style={{
                                        background: 'linear-gradient(to top,color-mix(in srgb, var(--code) 100%, transparent) 10%,transparent)',
                                        opacity: (getLineFromIndex(incorrectViewIdx) === getNumLines() - 1) ? 0 : 1,
                                        height: (getLineFromIndex(incorrectViewIdx) === 0 && getNumLines() > 2) ? "5rem" : "2.5rem",
                                    }}
                                />
                                <div className="pointer-events-none absolute bottom-0 left-0 w-full h-8 z-10 transition-all duration-200 ease-out"
                                    style={{
                                        backdropFilter: 'blur(1px)',
                                        maskImage: 'linear-gradient(to top, black 20%, transparent)', // bottom fade
                                        WebkitMaskImage: 'linear-gradient(to top, black 20%, transparent)',
                                        opacity: (getLineFromIndex(incorrectViewIdx) === getNumLines() - 1) ? 0 : 1,
                                        height: (getLineFromIndex(incorrectViewIdx) === 0 && getNumLines() > 2) ? "5rem" : "2.5rem",
                                    }}
                                />

                            </div>
                        </div>
                        <AIExplanation ref={aiRef} />
                    </div>
                    }
                </div>
            </div>
        </div >
    );
}
